/**
 * ============================================================================
 * HOMECROWD × SCHOOL ATHLETICS — Restaurant Partner Recruitment Landing Page
 * ============================================================================
 *
 * PURPOSE:
 *   Recruit local restaurants into the fan rewards program that lives inside
 *   the university athletics app. This page is sent by athletic departments
 *   directly to restaurant owners. It must clearly explain how the program
 *   works, what's required, and how the economics function.
 *
 *   Primary CTA: "Request to Join" form submission.
 *
 * ── CUSTOMIZATION GUIDE (CTO / Designer) ─────────────────────────────────────
 *
 *   BRAND COLORS    → Search "BRAND:" — all colors in constants below.
 *                      CSS variables in globalStyles mirror them automatically.
 *
 *   SCHOOL NAME     → Edit SCHOOL_NAME and SCHOOL_SHORT constants.
 *                      All copy references pull from these two values.
 *
 *   LOGOS           → Search "LOGO_PLACEHOLDER" (4 locations):
 *                        • Nav left:    Homecrowd logo
 *                        • Nav right:   School athletics mark
 *                        • Footer left: Homecrowd logo
 *                        • Footer right: School mark
 *
 *   APP IMAGES      → Search "IMAGE_PLACEHOLDER" (3 locations):
 *                        • Hero right:           Phone mockup / app screenshot
 *                        • What This Actually Is: App rewards UI screenshot
 *                        • Why It Matters:        Stadium / fan photo
 *
 *   COPY            → Each section is its own component. All user-facing strings
 *                      are inline. Key blocks marked with "COPY:" comments.
 *
 *   FORM HANDLING   → Search "FORM_ENDPOINT" in FormSection for API hookup.
 *
 *   FONTS           → Google Fonts via @import in globalStyles. Swap families
 *                      and update --font-display / --font-body variables.
 *
 *   SECTION ORDER   → Independent components composed in MerchantsPage().
 *                      Reorder, remove, or duplicate freely.
 *
 *   HEADLINE ALTS   → Stored in file header:
 *                        • Connect Your Restaurant With Bearcats Fans
 *                        • Join the Local Rewards Network Supporting Cincinnati Fans
 *                        • Be Part of Cincinnati's Fan Rewards Experience
 *
 * ── TECH ──────────────────────────────────────────────────────────────────────
 *   React + Tailwind CSS (core utility classes only). Zero external deps.
 *   Mobile-responsive. Single-file. Production-ready.
 * ============================================================================
 */

import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import apiService from "../services/api";
import { requestToJoinSchema } from "../schemas/requestToJoinSchema";
import merchantScreenshot from "../assets/images/merchant-screenshot.png";
import whiteLogo from "../assets/images/white-logo.png";

/* ┌──────────────────────────────────────────────────────────────────────────┐ */
/* │ BRAND CONSTANTS — Change these to rebrand for any school                 │ */
/* └──────────────────────────────────────────────────────────────────────────┘ */
const DEFAULT_SCHOOL_NAME = "Cincinnati Athletics";
const DEFAULT_SCHOOL_MASCOT = "Bearcats";
const DEFAULT_REWARDS_TITLE = "Cincinnati Rewards";
const DEFAULT_SCHOOL_COLOR_PRIMARY = "#E00122";
const DEFAULT_SCHOOL_COLOR_DARK = "#000000";
const DEFAULT_ACCENT_COLOR = "#E00122";
const DEFAULT_SCHOOL_MARK_TEXT = "UC";

function normalizeHexColor(input) {
  const raw = String(input ?? "").trim();
  if (!raw) return "";
  const withHash = raw.startsWith("#") ? raw : `#${raw}`;
  const six = /^#([0-9a-fA-F]{6})$/.exec(withHash);
  if (six) return `#${six[1].toUpperCase()}`;
  const three = /^#([0-9a-fA-F]{3})$/.exec(withHash);
  if (three) {
    const r = three[1][0];
    const g = three[1][1];
    const b = three[1][2];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  return "";
}

function sanitizeText(input, maxLen) {
  const v = String(input ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (!v) return "";
  return v.length > maxLen ? v.slice(0, maxLen) : v;
}

function normalizeLogoUrl(input) {
  const v = String(input ?? "").trim();
  if (!v) return "";
  const clipped = v.length > 50000 ? v.slice(0, 50000) : v;
  if (/^https?:\/\//i.test(clipped)) return clipped;
  if (/^data:image\//i.test(clipped)) return clipped;
  return "";
}


function getMarkTextFromText(text) {
  const v = sanitizeText(text, 40);
  if (!v) return "";

  // Words to skip when creating initials
  const skipWords = ['of', 'the', 'and', 'for', 'at', 'in', 'on', 'by', 'to', 'a', 'an'];

  const words = v.split(/\s+/)
    .filter(Boolean)
    .filter(word => !skipWords.includes(word.toLowerCase()));

  let letters = "";
  for (const w of words) {
    letters += w[0].toUpperCase();
    if (letters.length >= 3) break;
  }
  if (letters.length >= 2) return letters;
  const fallback = v.replace(/\s+/g, "").slice(0, 2).toUpperCase();
  return fallback || "";
}

function lightenColor(color, amount = 40) {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate brightness
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // If color is dark (brightness < 128), lighten it
  if (brightness < 128) {
    const newR = Math.min(255, r + amount);
    const newG = Math.min(255, g + amount);
    const newB = Math.min(255, b + amount);
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  }

  // If color is already light, return as is
  return color;
}

let SCHOOL_NAME = DEFAULT_SCHOOL_NAME;
let SCHOOL_MASCOT = DEFAULT_SCHOOL_MASCOT;
let SCHOOL_REWARDS_TITLE = DEFAULT_REWARDS_TITLE;
let SCHOOL_LOGO_URL = "";
let SCHOOL_MARK_TEXT = DEFAULT_SCHOOL_MARK_TEXT;
let SCHOOL_COLOR_PRIMARY = DEFAULT_SCHOOL_COLOR_PRIMARY;
let SCHOOL_COLOR_DARK = DEFAULT_SCHOOL_COLOR_DARK;
let ACCENT_COLOR = DEFAULT_ACCENT_COLOR;

function applySchoolBrandingConfig(cfg) {
  const nextSchoolName = sanitizeText(cfg?.schoolName, 120) || DEFAULT_SCHOOL_NAME;
  const nextMascotName = sanitizeText(cfg?.mascotName, 60) || DEFAULT_SCHOOL_MASCOT;
  const nextRewardsTitle = sanitizeText(cfg?.rewardsTitle, 80) || `${nextSchoolName} Rewards` || DEFAULT_REWARDS_TITLE;
  const nextPrimary = normalizeHexColor(cfg?.primaryColor) || DEFAULT_SCHOOL_COLOR_PRIMARY;
  const nextDark = normalizeHexColor(cfg?.darkColor) || DEFAULT_SCHOOL_COLOR_DARK;
  const nextAccent = normalizeHexColor(cfg?.accentColor) || nextPrimary || DEFAULT_ACCENT_COLOR;
  const nextLogoUrl = normalizeLogoUrl(cfg?.logoUrl) || "";

  SCHOOL_NAME = nextSchoolName;
  SCHOOL_MASCOT = nextMascotName;
  SCHOOL_REWARDS_TITLE = nextRewardsTitle;
  SCHOOL_LOGO_URL = nextLogoUrl;
  SCHOOL_MARK_TEXT = getMarkTextFromText(nextSchoolName) || DEFAULT_SCHOOL_MARK_TEXT;
  SCHOOL_COLOR_PRIMARY = nextPrimary;
  SCHOOL_COLOR_DARK = nextDark;
  ACCENT_COLOR = nextAccent;
}

/*
 * HOMECROWD LOGO — embedded as base64 PNGs extracted from the brand guide.
 * LOGO_WHITE: White logo on transparent bg — for dark backgrounds (hero, dark sections)
 * LOGO_DARK:  Dark logo on transparent bg — for light backgrounds (body sections, footer)
 *
 * To replace: swap these data URIs with hosted image URLs, e.g.:
 *   const HC_LOGO_WHITE = "/images/homecrowd-logo-white.svg";
 *   const HC_LOGO_DARK  = "/images/homecrowd-logo-dark.svg";
 *
 * Brand font: Baikal Extra Condensed Bold
 *   Not available on Google Fonts. Load via @font-face when font files are available.
 *   Currently using Fraunces as a serif display stand-in. Replace --font-display
 *   in globalStyles once Baikal is hosted.
 */
const HC_LOGO_WHITE = "data:image/png;base64,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";
const HC_LOGO_DARK = "data:image/png;base64,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";

/* ┌──────────────────────────────────────────────────────────────────────────┐ */
/* │ GLOBAL STYLES                                                            │ */
/* └──────────────────────────────────────────────────────────────────────────┘ */
function getGlobalStyles() {
  return `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=Fraunces:ital,opsz,wght@0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,500&display=swap');

  /*
   * BRAND FONT: Baikal Extra Condensed Bold
   * Uncomment and update the url() below once font files are hosted:
   *
   * @font-face {
   *   font-family: 'Baikal';
   *   font-weight: 700;
   *   font-style: normal;
   *   src: url('/fonts/Baikal-ExtraCondensedBold.woff2') format('woff2');
   * }
   *
   * Then change --font-display below to: 'Baikal', 'Fraunces', Georgia, serif;
   */

  :root {
    --color-primary: ${SCHOOL_COLOR_PRIMARY};
    --color-dark: ${SCHOOL_COLOR_DARK};
    --color-accent: ${ACCENT_COLOR};
    --font-display: 'Fraunces', Georgia, serif;  /* BRAND: Replace with 'Baikal' when hosted */
    --font-body: 'DM Sans', system-ui, sans-serif;
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }
  html { scroll-behavior: smooth; }

  .fade-section {
    opacity: 0;
    transform: translateY(24px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .fade-section.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;
}

/* ┌──────────────────────────────────────────────────────────────────────────┐ */
/* │ SCROLL ANIMATION HOOK                                                    │ */
/* └──────────────────────────────────────────────────────────────────────────┘ */
function useScrollFade() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 1 — HERO                                                       ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function HeroSection() {
  return (
    <>
      {/* ── NAV ── */}
      <nav className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Homecrowd logo */}
            <div className="flex items-center gap-4">
              <img
                src={HC_LOGO_DARK}
                alt="Homecrowd"
                className="h-8 w-auto object-contain"
              />
              <div className="hidden sm:block w-px h-8 bg-gray-300"></div>
              <span className="hidden sm:block text-sm text-gray-500">Partner Program</span>
            </div>

            {/* Right side - School branding */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {SCHOOL_LOGO_URL ? (
                  <img
                    src={SCHOOL_LOGO_URL}
                    alt={`${SCHOOL_NAME} logo`}
                    className="h-8 w-auto object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-gray-50 border border-gray-300"
                  style={{
                    color: SCHOOL_COLOR_PRIMARY,
                    display: SCHOOL_LOGO_URL ? 'none' : 'flex'
                  }}
                >
                  {SCHOOL_MARK_TEXT}
                </div>
                <span className="font-medium text-gray-900 text-sm">{SCHOOL_NAME}</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Dark Background */}
      <section
        className="relative overflow-hidden"
        style={{
          fontFamily: "var(--font-body)",
          background: `linear-gradient(145deg, ${SCHOOL_COLOR_DARK} 0%, #1a1a1a 60%, ${SCHOOL_COLOR_PRIMARY}22 100%)`,
          minHeight: "calc(100vh - 65px)",
        }}
      >
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />

        {/* ── HERO CONTENT ── */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="max-w-3xl">
          {/* COPY: Reading time */}
          <p className="text-white/40 text-sm italic mb-6">4 minute read</p>

          <div
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium mb-8"
            style={{ background: `${lightenColor(SCHOOL_COLOR_PRIMARY)}18`, color: lightenColor(SCHOOL_COLOR_PRIMARY), border: `1px solid ${lightenColor(SCHOOL_COLOR_PRIMARY)}30` }}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: lightenColor(SCHOOL_COLOR_PRIMARY) }} />
            Official {SCHOOL_NAME} Partner Program
          </div>

          {/* COPY: Primary headline */}
          <h1
            className="text-white text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6"
            style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.02em" }}
          >
            Get Your Restaurant in Front of{" "}
            <span style={{ color: lightenColor(SCHOOL_COLOR_PRIMARY) }}>{SCHOOL_MASCOT}</span>{" "}
            Fans — Inside the Official Athletics App
          </h1>

          {/* COPY: Subheadline — concrete value prop in one sentence */}
          <p className="text-white/70 text-lg md:text-xl leading-relaxed max-w-xl mb-10">
            Fans discover your restaurant in the {SCHOOL_NAME} app, pay with
            their normal card, and earn {SCHOOL_REWARDS_TITLE} automatically. You only pay a
            3% fee when they actually spend.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#request-to-join"
              className="inline-flex items-center justify-center rounded-lg px-8 py-4 text-white font-semibold text-base transition-all duration-200 hover:brightness-110 hover:scale-105"
              style={{ background: SCHOOL_COLOR_PRIMARY }}
            >
              Request to Join
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center rounded-lg px-8 py-4 text-white/80 font-medium text-base border border-white/20 transition-all duration-200 hover:bg-white/5"
            >
              See How It Works
            </a>
          </div>
        </div>

        {/* IMAGE_PLACEHOLDER: App mockup showing rewards in athletics app */}
        <div className="hidden lg:block absolute right-10 top-1/2 -translate-y-1/2 w-72">
          <div className="w-full rounded-2xl border border-white/10 overflow-hidden" style={{ background: "rgba(255,255,255,0.03)" }}>
            <img
              src={merchantScreenshot}
              alt="Athletics app screenshot showing rewards"
              className="w-full h-auto rounded-2xl"
            />
          </div>
        </div>
      </div>

        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${SCHOOL_COLOR_PRIMARY}40, transparent)` }} />
      </section>
    </>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 2A — VALUE STRIP (high-visibility, directly below hero)        ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function ValueStrip() {
  const cards = [
    { label: "Inside the official athletics app" },
    { label: "No monthly fees" },
    { label: "No staff training required" },
    { label: "Only pay when fans spend" },
  ];

  return (
    <section
      className="py-8 md:py-10 px-6 md:px-10 border-b border-gray-100"
      style={{ fontFamily: "var(--font-body)", background: "#fff" }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {cards.map((c, i) => (
            <div
              key={i}
              className="flex flex-col items-center text-center p-5 rounded-xl border border-gray-100 bg-gray-50"
            >
              <p className="text-sm font-semibold text-gray-800 leading-snug">{c.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 2B — WHY RESTAURANTS PARTICIPATE                               ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function WhyParticipateSection() {
  const ref = useScrollFade();

  const benefits = [
    {
      title: "Featured in the Official App",
      description: `Your restaurant appears in the ${SCHOOL_NAME} app — the same app fans use for tickets, scores, and game-day content.`,
    },
    {
      title: "Pay Only When Fans Spend",
      description: "No upfront fees, no subscriptions, no setup costs. A 3% fee applies only to verified transactions from fans the program sends to you.",
    },
    {
      title: "Zero Operational Changes",
      description: `No coupons, no QR codes, no POS changes, no staff training. Fans pay with their normal card. ${SCHOOL_REWARDS_TITLE} happen automatically.`,
    },
    {
      title: "Influence Where Fans Eat",
      description: `Participating restaurants appear inside the ${SCHOOL_NAME} app when fans are deciding where to eat before and after games.`,
    },
  ];

  return (
    <section ref={ref} className="fade-section py-20 md:py-28 px-6 md:px-10" style={{ fontFamily: "var(--font-body)", background: "#FAFAF8" }}>
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: SCHOOL_COLOR_PRIMARY }}>
            Why Restaurants Participate
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4" style={{ fontFamily: "var(--font-display)" }}>
            Why local restaurants join the program.
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Homecrowd connects your restaurant directly to {SCHOOL_NAME} fans inside the
            official athletics app — tapping into the loyalty around the program. Performance-based,
            low-lift, and nothing new for your staff.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {benefits.map((b, i) => (
            <div key={i} className="group bg-white rounded-xl p-8 border border-gray-100 transition-all duration-300 hover:shadow-lg hover:border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "var(--font-display)" }}>{b.title}</h3>
              <p className="text-gray-600 leading-relaxed">{b.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 3 — WHAT THIS ACTUALLY IS                                      ║ */
/* ║  Clear explanation of the card-linked rewards mechanics                  ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function WhatThisIsSection() {
  const ref = useScrollFade();

  return (
    <section ref={ref} className="fade-section py-20 md:py-28 px-6 md:px-10" style={{ fontFamily: "var(--font-body)", background: "#fff" }}>
      <div className="max-w-6xl mx-auto">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: SCHOOL_COLOR_PRIMARY }}>
            What This Actually Is
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-6" style={{ fontFamily: "var(--font-display)" }}>
            The {SCHOOL_REWARDS_TITLE} experience inside the {SCHOOL_NAME} athletics app.
          </h2>
          <div className="space-y-4 text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
            {/* COPY: Core mechanics — tightened */}
            <p>
              Fans connect a debit or credit card inside the official {SCHOOL_NAME} app.
              When they spend at your restaurant, the transaction is verified automatically
              through Visa and Mastercard. The fan earns {SCHOOL_REWARDS_TITLE} — unlocking
              access to unique and exclusive experiences with {SCHOOL_NAME}. You do nothing
              different at the register.
            </p>
            <p>
              No coupons. No QR codes. No POS changes. No extra work for staff.
            </p>
          </div>
        </div>

        {/* Trust badges — payment network verification */}
        <div className="mt-16 pt-10 border-t border-gray-100">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 text-sm text-gray-400">
            <span>Transactions verified securely through:</span>
            <div className="flex items-center gap-6">
              {/* BRAND: Replace with actual Visa/MC logos if desired */}
              <span className="font-semibold text-gray-500 tracking-wide">VISA</span>
              <span className="font-semibold text-gray-500 tracking-wide">Mastercard</span>
            </div>
          </div>
          <p className="text-center text-xs text-gray-400 mt-3">
            No card information is ever shared with participating restaurants.
          </p>
        </div>
      </div>
    </section>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 4 — HOW IT WORKS (with visual flow diagram)                    ║ */
/* ║  Step-by-step: app → card → discover → spend → rewards                  ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function HowItWorksSection() {
  const ref = useScrollFade();

  const flowSteps = [
    { label: "Fan opens the athletics app", icon: "📱" },
    { label: "Connects a debit or credit card", icon: "💳" },
    { label: "Sees participating restaurants on the map", icon: "📍" },
    { label: "Spends normally at your restaurant", icon: "🍽️" },
    { label: `${SCHOOL_REWARDS_TITLE} triggered automatically`, icon: "✦" },
  ];

  return (
    <section id="how-it-works" ref={ref} className="fade-section py-20 md:py-28 px-6 md:px-10" style={{ fontFamily: "var(--font-body)", background: "#FAFAF8" }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: SCHOOL_COLOR_PRIMARY }}>
            How It Works
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4" style={{ fontFamily: "var(--font-display)" }}>
            The fan experience, from start to finish.
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Nothing changes for your staff. The entire {SCHOOL_REWARDS_TITLE} flow happens
            inside the app and through the payment network.
          </p>
        </div>

        {/* ── VISUAL FLOW DIAGRAM ── */}
        {/* Desktop: horizontal flow with arrows. Mobile: vertical stack. */}
        <div className="bg-white rounded-2xl border border-gray-100 p-8 md:p-12">

          {/* Desktop flow — horizontal */}
          <div className="hidden md:flex items-stretch justify-between gap-0">
            {flowSteps.map((step, i) => (
              <div key={i} className="flex items-center flex-1">
                {/* Step card */}
                <div className="flex flex-col items-center text-center flex-1">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl mb-4 transition-transform duration-200 hover:scale-110"
                    style={{ background: `${SCHOOL_COLOR_PRIMARY}10` }}
                  >
                    {step.icon}
                  </div>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold mb-3"
                    style={{ background: SCHOOL_COLOR_PRIMARY }}
                  >
                    {i + 1}
                  </div>
                  <p className="text-sm text-gray-700 font-medium leading-snug max-w-28">{step.label}</p>
                </div>
                {/* Arrow between steps */}
                {i < flowSteps.length - 1 && (
                  <div className="flex items-center px-1 -mt-8">
                    <div className="w-8 h-px" style={{ background: `${SCHOOL_COLOR_PRIMARY}30` }} />
                    <div
                      className="w-0 h-0 border-t-4 border-b-4 border-l-6"
                      style={{
                        borderTopColor: "transparent",
                        borderBottomColor: "transparent",
                        borderLeftColor: `${SCHOOL_COLOR_PRIMARY}40`,
                        borderLeftWidth: "8px",
                        borderTopWidth: "5px",
                        borderBottomWidth: "5px",
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile flow — vertical */}
          <div className="md:hidden space-y-0">
            {flowSteps.map((step, i) => (
              <div key={i}>
                <div className="flex items-center gap-4 py-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-xl shrink-0"
                    style={{ background: `${SCHOOL_COLOR_PRIMARY}10` }}
                  >
                    {step.icon}
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                      style={{ background: SCHOOL_COLOR_PRIMARY }}
                    >
                      {i + 1}
                    </div>
                    <p className="text-sm text-gray-700 font-medium">{step.label}</p>
                  </div>
                </div>
                {/* Vertical connector */}
                {i < flowSteps.length - 1 && (
                  <div className="flex justify-center">
                    <div className="w-px h-4" style={{ background: `${SCHOOL_COLOR_PRIMARY}25` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Callout */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            Your restaurant doesn't need to change anything at the register.
            The fan pays like any other customer.
          </p>
        </div>
      </div>
    </section>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 5 — WHAT WE NEED FROM PARTICIPATING RESTAURANTS                ║ */
/* ║  MID, address, logo — explained in plain language                       ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function WhatWeNeedSection() {
  const ref = useScrollFade();

  const requirements = [
    {
      title: "Your Restaurant Address",
      description: "We place your restaurant on the partner map inside the athletics app so fans can find you by location.",
    },
    {
      title: "Your Logo or Brand Image",
      description: "Displayed alongside your listing in the app. A clean logo or strong brand photo works best.",
    },
    {
      title: "Sign the MOU Onboarding Doc",
      description: "A simple agreement that outlines the partnership terms, quick and painless.",
    },
  ];

  return (
    <section ref={ref} className="fade-section py-20 md:py-28 px-6 md:px-10" style={{ fontFamily: "var(--font-body)", background: "#fff" }}>
      <div className="max-w-4xl mx-auto">
        <div className="max-w-2xl mb-14">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: SCHOOL_COLOR_PRIMARY }}>
            What We Need
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4" style={{ fontFamily: "var(--font-display)" }}>
            Three things to get your restaurant activated.
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Once you submit a request to join, our team will walk you through
            collecting these items. Most restaurants are fully activated within
            a week or two.
          </p>
        </div>

        <div className="space-y-6">
          {requirements.map((r, i) => (
            <div key={i} className="flex gap-5 md:gap-6 bg-gray-50 rounded-xl p-6 md:p-8 border border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1" style={{ fontFamily: "var(--font-display)" }}>
                  {r.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base">{r.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Reassurance note */}
        <div className="mt-8 p-5 rounded-lg border border-gray-100 bg-white">
          <p className="text-gray-500 text-sm leading-relaxed text-center">
            Our team walks you through everything after you submit. Most restaurants
            are activated in 10 days.
          </p>
        </div>
      </div>
    </section>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 6 — HOW THE 3% PERFORMANCE FEE WORKS                          ║ */
/* ║  Economics explained clearly with a concrete example                    ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function PricingSection() {
  const ref = useScrollFade();

  return (
    <section
      ref={ref}
      className="fade-section py-20 md:py-28 px-6 md:px-10"
      style={{
        fontFamily: "var(--font-body)",
        background: `linear-gradient(170deg, ${SCHOOL_COLOR_DARK} 0%, #1c1c1c 100%)`,
      }}
    >
      <div className="max-w-4xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: lightenColor(SCHOOL_COLOR_PRIMARY) }}>
            Pricing
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "var(--font-display)" }}>
            How the 3% Performance Fee Works
          </h2>
          <p className="text-white/60 text-lg leading-relaxed">
            You pay nothing unless fans spend money at your restaurant through
            the program. That's the entire model.
          </p>
        </div>

        {/* Fee structure card */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 md:p-10 mb-8">
          {/* The fee explained */}
          <div className="text-center mb-10">
            <div
              className="inline-flex items-baseline gap-1 mb-3"
              style={{ fontFamily: "var(--font-display)" }}
            >
              <span className="text-6xl md:text-7xl font-bold" style={{ color: lightenColor(SCHOOL_COLOR_PRIMARY) }}>3</span>
              <span className="text-3xl md:text-4xl font-bold" style={{ color: lightenColor(SCHOOL_COLOR_PRIMARY) }}>%</span>
            </div>
            <p className="text-white/70 text-base max-w-md mx-auto">
              performance marketing fee on verified transactions from fans
              that Homecrowd sends to your restaurant through the program.
            </p>
          </div>

          {/* Three "no" callouts */}
          <div className="grid md:grid-cols-3 gap-4 mb-10">
            {[
              { label: "No upfront fees", detail: "Nothing to pay to get started" },
              { label: "No monthly subscription", detail: "No recurring charges, ever" },
              { label: "No setup costs", detail: "We handle onboarding at no cost" },
            ].map((item, i) => (
              <div key={i} className="text-center p-5 rounded-xl bg-white/5 border border-white/10">
                <p className="text-white font-semibold text-sm mb-1">{item.label}</p>
                <p className="text-white/40 text-xs">{item.detail}</p>
              </div>
            ))}
          </div>

          {/* Concrete example */}
          <div className="rounded-xl bg-white/5 border border-white/10 p-6 md:p-8">
            <p className="text-white/50 text-xs font-semibold uppercase tracking-widest mb-4">Example</p>
            <p className="text-white/80 text-sm leading-relaxed mb-6 text-center max-w-lg mx-auto">
              A {SCHOOL_NAME} fan discovers your restaurant through the athletics app
              and spends <span className="text-white font-semibold">$50 on dinner</span>.
              The transaction is verified automatically through the payment network.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <div className="text-center p-4 rounded-lg bg-white/5 border border-white/10 min-w-32">
                <p className="text-white/50 text-xs mb-1">Your fee</p>
                <p className="text-2xl font-bold" style={{ color: lightenColor(SCHOOL_COLOR_PRIMARY), fontFamily: "var(--font-display)" }}>$1.50</p>
                <p className="text-white/40 text-xs mt-1">3% of $50</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-white/5 border border-white/10 min-w-32">
                <p className="text-white/50 text-xs mb-1">You keep</p>
                <p className="text-2xl font-bold text-green-400" style={{ fontFamily: "var(--font-display)" }}>$48.50</p>
                <p className="text-white/40 text-xs mt-1">97% of $50</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-white/5 border border-white/10 min-w-32">
                <p className="text-white/50 text-xs mb-1">Fan earns</p>
                <p className="text-2xl font-bold text-green-400" style={{ fontFamily: "var(--font-display)" }}>50</p>
                <p className="text-white/40 text-xs mt-1">{SCHOOL_MASCOT} Points</p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-white/50 text-sm font-medium mt-8">
          No spend, no fee. If the program doesn't send fans to your restaurant, you pay nothing.
        </p>
      </div>
    </section>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 7 — FAQ                                                        ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function FAQSection() {
  const ref = useScrollFade();
  const [openIndex, setOpenIndex] = useState(null);

  /*
   * COPY: FAQ pairs — keep honest and grounded.
   * Do NOT add self-serve, dashboard, or guaranteed traffic language.
   */
  const faqs = [
    {
      q: "How does the transaction verification work?",
      a: "When a fan links their card in the athletics app and spends at your restaurant, the transaction is verified automatically through Visa and Mastercard. No card information is ever shared with your restaurant.",
    },
    {
      q: "How much work is required from our team?",
      a: `Very little. We need your address, a logo, and a signed MOU to activate you. After that, there's nothing to manage. Fans pay with their normal card and ${SCHOOL_REWARDS_TITLE} happen automatically.`,
    },
    {
      q: "Can we create and manage our own custom offers?",
      a: `Not at this stage. Homecrowd manages ${SCHOOL_REWARDS_TITLE} structure and activation directly with participating restaurants.`,
    },
    {
      q: "Does submitting this form mean we're fully enrolled?",
      a: "No. It's a request to join. Our team will follow up with next steps and help you get activated.",
    },
  ];

  return (
    <section ref={ref} className="fade-section py-20 md:py-28 px-6 md:px-10" style={{ fontFamily: "var(--font-body)", background: "#FAFAF8" }}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: SCHOOL_COLOR_PRIMARY }}>FAQ</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight" style={{ fontFamily: "var(--font-display)" }}>
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-0">
          {faqs.map((faq, i) => (
            <div key={i} className="border-t border-gray-200 last:border-b">
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between py-6 text-left cursor-pointer"
              >
                <span className="text-gray-900 font-semibold text-base pr-4">{faq.q}</span>
                <span
                  className="text-2xl shrink-0 transition-transform duration-200"
                  style={{ color: SCHOOL_COLOR_PRIMARY, transform: openIndex === i ? "rotate(45deg)" : "rotate(0deg)" }}
                >+</span>
              </button>
              <div
                className="overflow-hidden transition-all duration-300"
                style={{ maxHeight: openIndex === i ? "280px" : "0px", opacity: openIndex === i ? 1 : 0 }}
              >
                <p className="text-gray-600 leading-relaxed pb-6 pr-12">{faq.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 7B — CREDIBILITY BAR                                           ║ */
/* ║  Reinforces official partnership — feels like an invitation, not a pitch ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function CredibilitySection() {
  return (
    <section
      className="py-12 md:py-16 px-6 md:px-10"
      style={{ fontFamily: "var(--font-body)", background: "#fff" }}
    >
      <div className="max-w-3xl mx-auto text-center">
        <div
          className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium mb-5"
          style={{ background: `${SCHOOL_COLOR_PRIMARY}10`, color: SCHOOL_COLOR_PRIMARY, border: `1px solid ${SCHOOL_COLOR_PRIMARY}20` }}
        >
          <span className="w-2 h-2 rounded-full" style={{ background: SCHOOL_COLOR_PRIMARY }} />
          Official Partner Invitation
        </div>
        <p className="text-gray-600 text-base leading-relaxed max-w-lg mx-auto">
          This page was shared with you by {SCHOOL_NAME} as part of their
          official {SCHOOL_REWARDS_TITLE} initiative with Homecrowd. It's intended for
          local restaurant partners interested in participating in the program.
        </p>
      </div>
    </section>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 8 — REQUEST TO JOIN FORM                                       ║ */
/* ║  Includes "What Happens Next" steps above the form fields               ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function FormSection({ schoolId }) {
  const ref = useScrollFade();
  const [formData, setFormData] = useState({
    restaurantName: "",
    contactName: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingError, setSubmittingError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      return { ...prev, [name]: "" };
    });
  };

  const handleSubmit = async () => {
    if (!schoolId) {
      setSubmittingError("School configuration is required.");
      return;
    }

    const parsed = requestToJoinSchema.safeParse(formData);
    if (!parsed.success) {
      const { fieldErrors: schemaFieldErrors } = parsed.error.flatten();
      setFieldErrors({
        restaurantName: schemaFieldErrors.restaurantName?.[0] || "",
        contactName: schemaFieldErrors.contactName?.[0] || "",
        email: schemaFieldErrors.email?.[0] || "",
        phone: schemaFieldErrors.phone?.[0] || "",
      });
      return;
    }

    setFieldErrors({});
    setSubmitting(true);
    setSubmittingError(null);
    try {
      await apiService.submitMerchantInterest(schoolId, {
        restaurant_name: parsed.data.restaurantName,
        contact_name: parsed.data.contactName,
        email: parsed.data.email,
        phone: parsed.data.phone,
        notes: parsed.data.notes,
      });
      setSubmitted(true);
    } catch (e) {
      setSubmittingError(e.response?.data?.error || e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="request-to-join" ref={ref} className="fade-section py-20 md:py-28 px-6 md:px-10" style={{ fontFamily: "var(--font-body)", background: "#fff" }}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: SCHOOL_COLOR_PRIMARY }}>Get Started</p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4" style={{ fontFamily: "var(--font-display)" }}>
            Request to Join
          </h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Interested in participating? Share your information and our team
            will follow up with next steps.
          </p>
        </div>

        {/* ── WHAT HAPPENS NEXT ── */}
        <div className="mb-10 grid md:grid-cols-3 gap-4">
          {[
            { num: "1", label: "Submit your interest", detail: "Fill out the form below" },
            { num: "2", label: "We follow up", detail: "Collect onboarding details" },
            { num: "3", label: "You're activated", detail: "Restaurant reviewed and live" },
          ].map((step, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 border border-gray-100">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5"
                style={{ background: SCHOOL_COLOR_PRIMARY }}
              >
                {step.num}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{step.label}</p>
                <p className="text-xs text-gray-500">{step.detail}</p>
              </div>
            </div>
          ))}
        </div>

        {submitted ? (
          <div className="text-center py-16 px-8 rounded-2xl" style={{ background: `${SCHOOL_COLOR_PRIMARY}08` }}>
            <div className="text-5xl mb-4">✓</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "var(--font-display)" }}>
              Thanks — we've received your request.
            </h3>
            <p className="text-gray-600 max-w-sm mx-auto">We'll follow up soon.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-100 p-8 md:p-10" style={{ background: "#FAFAF8" }}>
            <div className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="Restaurant Name" name="restaurantName" value={formData.restaurantName} onChange={handleChange} required error={fieldErrors.restaurantName} />
                <FormField label="Contact Name" name="contactName" value={formData.contactName} onChange={handleChange} required error={fieldErrors.contactName} />
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required error={fieldErrors.email} />
                <FormField label="Phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} error={fieldErrors.phone} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Notes / Questions <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  name="notes" rows={3} value={formData.notes} onChange={handleChange}
                  className="w-full rounded-lg border border-gray-200 px-4 py-3 text-gray-900 text-base focus:outline-none transition-shadow resize-none"
                  onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${SCHOOL_COLOR_PRIMARY}40`)}
                  onBlur={(e) => (e.target.style.boxShadow = "none")}
                />
              </div>
              {submittingError && (
                <p className="text-red-600 text-sm" role="alert">{submittingError}</p>
              )}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full rounded-lg px-8 py-4 text-white font-semibold text-base transition-all duration-200 hover:brightness-110 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{ background: SCHOOL_COLOR_PRIMARY }}
              >
                {submitting ? "Submitting…" : "Request to Join"}
              </button>
              <p className="text-center text-gray-400 text-xs leading-relaxed">
                By submitting, you agree to be contacted by the Homecrowd team
                about the {SCHOOL_NAME} partner program. We will not share your
                information with third parties.
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Reusable form field ── */
function FormField({ label, name, type = "text", value, onChange, required, placeholder, error }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        {required && <span style={{ color: SCHOOL_COLOR_PRIMARY }}> *</span>}
      </label>
      <input
        type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full rounded-lg border border-gray-200 px-4 py-3 text-gray-900 text-base focus:outline-none transition-shadow"
        onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${SCHOOL_COLOR_PRIMARY}40`)}
        onBlur={(e) => (e.target.style.boxShadow = "none")}
      />
      {error && (
        <p className="text-red-600 text-sm mt-1" role="alert">{error}</p>
      )}
    </div>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  SECTION 9 — FOOTER                                                     ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
function FooterSection() {
  return (
    <footer className="py-12 px-6 md:px-10 border-t border-gray-100" style={{ fontFamily: "var(--font-body)", background: "#fff" }}>
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          {/* Homecrowd logo — dark version for light background */}
          <div className="flex items-center gap-2">
            <img
              src={HC_LOGO_DARK}
              alt="Homecrowd"
              style={{ height: 28 }}
              className="object-contain"
            />
          </div>
          <span className="text-gray-300">×</span>
          {/* LOGO_PLACEHOLDER: School footer mark */}
          {SCHOOL_LOGO_URL ? (
            <img
              src={SCHOOL_LOGO_URL}
              alt={`${SCHOOL_NAME} logo`}
              className="h-6 w-auto object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <span
            className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-500"
            style={{
              color: SCHOOL_COLOR_PRIMARY,
              display: SCHOOL_LOGO_URL ? 'none' : 'flex'
            }}
          >
            {SCHOOL_MARK_TEXT}
          </span>
          <span className="text-gray-500 text-sm">{SCHOOL_NAME}</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-gray-400">
          <a href="/privacy" className="hover:text-gray-600 transition-colors">Privacy</a>
          <a href="/terms" className="hover:text-gray-600 transition-colors">Terms</a>
          <a href="/contact" className="hover:text-gray-600 transition-colors">Contact</a>
          <span>© {new Date().getFullYear()} Homecrowd</span>
        </div>
      </div>
    </footer>
  );
}


/* ╔══════════════════════════════════════════════════════════════════════════╗ */
/* ║  MAIN PAGE — Compose all 9 sections                                     ║ */
/* ╚══════════════════════════════════════════════════════════════════════════╝ */
const MerchantsPage = () => {
  const { schoolId } = useParams();

  const [brandReady, setBrandReady] = useState(!schoolId);
  const [pageVisible, setPageVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!schoolId) {
        setBrandReady(true);
        return;
      }

      try {
        const cfg = await apiService.getMerchantsPageConfig(schoolId);
        if (cancelled) return;
        applySchoolBrandingConfig(cfg);
      } catch (e) {
        if (!cancelled) setBrandReady(true);
        return;
      }

      if (!cancelled) setBrandReady(true);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  useEffect(() => {
    if (!brandReady) return;
    setPageVisible(false);
    const id = requestAnimationFrame(() => setPageVisible(true));
    return () => cancelAnimationFrame(id);
  }, [brandReady]);

  if (!brandReady) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#fff" }}>
        <div
          className="animate-spin rounded-full border-4 border-gray-200"
          style={{ width: 56, height: 56, borderTopColor: "#26CDFF" }}
          aria-label="Loading school branding"
          role="status"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#fff" }}>
      <style>{getGlobalStyles()}</style>

      <div style={{ opacity: pageVisible ? 1 : 0, transition: "opacity 260ms ease" }}>
        <HeroSection />            {/* 1. Hero */}
        <ValueStrip />             {/* 2. High-visibility value cards */}
        <WhatThisIsSection />       {/* 3. What This Actually Is */}
        <WhyParticipateSection />   {/* 4. Why Restaurants Participate */}
        <HowItWorksSection />       {/* 5. How It Works (with flow diagram) */}
        <WhatWeNeedSection />       {/* 6. What We Need From You */}
        <PricingSection />          {/* 7. How the 3% Fee Works */}
        <FAQSection />              {/* 8. FAQ */}
        <CredibilitySection />      {/* 9. Credibility / official invitation */}
        <FormSection schoolId={schoolId} />             {/* 10. Request to Join (with What Happens Next) */}
        <FooterSection />           {/* 11. Footer */}
      </div>
    </div>
  );
}

export default MerchantsPage;